import React, {useContext, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import UserContext from '../../../../Contexts/UserContext/UserContext';
import {formatInvoiceNumber, Invoice, InvoiceDetail, InvoiceEmitter, InvoiceListResponse, InvoiceProduct, InvoiceService} from '../../../../services/invoiceService';
import type {VatBreakdown, XRechnungInvoiceData, XRechnungLineItem, XRechnungOptions} from '../Tasks/utils/xrechnung';
import {DEFAULT_PAYMENT_DAYS, XRECHNUNG_SPEC_ID,} from '../Tasks/utils/xrechnung';
import {createInvoice, createInvoiceFromTemplate, InvoiceData, InvoiceLineItem as InvoiceGeneratorLineItem} from '../Tasks/utils/invoiceGenerator';
import EmitterSelectDialog from './EmitterSelectDialog';
import InvoiceCreateDialog from './InvoiceCreateDialog';
import InvoiceViewEditDialog from './InvoiceViewEditDialog';

// Status color mapping
const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
        case 'DRAFT':
            return 'default';
        case 'FINALIZED':
            return 'info';
        case 'SENT':
            return 'warning';
        case 'PAID':
            return 'success';
        case 'CANCELLED':
            return 'error';
        default:
            return 'default';
    }
};

// Status label mapping
const getStatusLabel = (status: string): string => {
    switch (status) {
        case 'DRAFT':
            return 'Entwurf';
        case 'FINALIZED':
            return 'Finalisiert';
        case 'SENT':
            return 'Versendet';
        case 'PAID':
            return 'Bezahlt';
        case 'CANCELLED':
            return 'Storniert';
        default:
            return status;
    }
};

// Format date for display
const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

// Format currency
const formatCurrency = (amount?: number): string => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

const RechnungenTab: React.FC = () => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const {user_roll} = useContext(UserContext);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [emitters, setEmitters] = useState<InvoiceEmitter[]>([]);
    const [emitterMap, setEmitterMap] = useState<Map<number, InvoiceEmitter>>(new Map());
    const [products, setProducts] = useState<InvoiceProduct[]>([]);
    const [invoiceDetails, setInvoiceDetails] = useState<Map<number, InvoiceDetail>>(new Map());

    // Check if user is Administrator
    const isAdministrator = user_roll?.includes('Administrator') || false;

    // Dialog states
    const [emitterSelectOpen, setEmitterSelectOpen] = useState(false);
    const [invoiceCreateOpen, setInvoiceCreateOpen] = useState(false);
    const [selectedEmitter, setSelectedEmitter] = useState<InvoiceEmitter | null>(null);

    // View/Edit dialog state
    const [viewEditOpen, setViewEditOpen] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
    const [deleting, setDeleting] = useState(false);

    // PDF generation state
    const [generatingPdfFor, setGeneratingPdfFor] = useState<number | null>(null);

    // Status menu state
    const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
    const [statusMenuInvoice, setStatusMenuInvoice] = useState<Invoice | null>(null);

    // Storno (cancellation) confirmation dialog state
    const [stornoDialogOpen, setStornoDialogOpen] = useState(false);
    const [invoiceToStorno, setInvoiceToStorno] = useState<Invoice | null>(null);
    const [creatingStorno, setCreatingStorno] = useState(false);
    const [stornoInvoiceNumber, setStornoInvoiceNumber] = useState<string>('');

    // Context menu state (right-click)
    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        invoice: Invoice;
    } | null>(null);

    // Permanent delete confirmation dialog state (Administrator only)
    const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
    const [invoiceToPermanentDelete, setInvoiceToPermanentDelete] = useState<Invoice | null>(null);
    const [permanentDeleting, setPermanentDeleting] = useState(false);

    // Filter state
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');

    // Pagination state
    const PAGE_SIZE = 25;
    const [totalInvoices, setTotalInvoices] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(0); // 0-indexed for MUI TablePagination

    // All available statuses
    const allStatuses = ['DRAFT', 'FINALIZED', 'SENT', 'PAID', 'CANCELLED'];

    // Check if any filter is active (status or date)
    const hasActiveServerFilters = statusFilter.length > 0 || dateFrom || dateTo;

    // Track if this is the initial mount
    const isInitialMount = React.useRef(true);

    useEffect(() => {
        loadInvoices();
        loadEmitters();
        loadProducts();
    }, []);

    // Reload invoices when server-side filters change (skip initial mount)
    // Reset to first page when filters change
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        setCurrentPage(0);
        loadInvoices(0);
    }, [statusFilter, dateFrom, dateTo]);

    const loadProducts = () => {
        call_backend_api(
            InvoiceService.getProducts(true), // active products only
            (data: InvoiceProduct[]) => {
                setProducts(data);
            },
            (err: any) => {
                console.error('Error loading products:', err);
            },
            undefined,
            false
        );
    };

    const loadEmitters = () => {
        call_backend_api(
            InvoiceService.getEmitters(),
            (data: InvoiceEmitter[]) => {
                setEmitters(data);
                const map = new Map<number, InvoiceEmitter>();
                data.forEach(e => map.set(e.id, e));
                setEmitterMap(map);
            },
            (err: any) => {
                console.error('Error loading emitters:', err);
            },
            undefined,
            false
        );
    };

    // Get emitter short_key for an invoice
    const getEmitterShortKey = (emitterId: number): string => {
        return emitterMap.get(emitterId)?.short_key || '';
    };

    // Handle new invoice button click
    const handleNewInvoice = () => {
        setEmitterSelectOpen(true);
    };

    // Handle emitter selection
    const handleEmitterSelect = (emitter: InvoiceEmitter) => {
        setSelectedEmitter(emitter);
        setEmitterSelectOpen(false);
        setInvoiceCreateOpen(true);
    };

    // Handle change emitter (from create dialog)
    const handleChangeEmitter = () => {
        setInvoiceCreateOpen(false);
        setEmitterSelectOpen(true);
    };

    // Handle invoice created
    const handleInvoiceSaved = () => {
        setInvoiceCreateOpen(false);
        setSelectedEmitter(null);
        loadInvoices();
    };

    // Handle dialogs closed
    const handleEmitterSelectClose = () => {
        setEmitterSelectOpen(false);
    };

    const handleInvoiceCreateClose = () => {
        setInvoiceCreateOpen(false);
        setSelectedEmitter(null);
    };

    // Handle invoice row click
    const handleInvoiceClick = (invoiceId: number) => {
        setSelectedInvoiceId(invoiceId);
        setViewEditOpen(true);
    };

    // Handle view/edit dialog close
    const handleViewEditClose = () => {
        setViewEditOpen(false);
        setSelectedInvoiceId(null);
    };

    // Handle invoice saved in view/edit dialog
    const handleViewEditSave = () => {
        setViewEditOpen(false);
        setSelectedInvoiceId(null);
        loadInvoices();
    };

    // Handle invoice deleted
    const handleViewEditDelete = () => {
        setViewEditOpen(false);
        setSelectedInvoiceId(null);
        loadInvoices();
    };

    // Handle delete icon click (from list)
    const handleDeleteClick = (event: React.MouseEvent, invoice: Invoice) => {
        event.stopPropagation(); // Prevent row click
        setInvoiceToDelete(invoice);
        setDeleteDialogOpen(true);
    };

    // Handle delete confirmation
    const handleConfirmDelete = () => {
        if (!invoiceToDelete) return;

        setDeleting(true);
        call_backend_api(
            InvoiceService.archiveInvoice(invoiceToDelete.id),
            () => {
                setDeleting(false);
                setDeleteDialogOpen(false);
                setInvoiceToDelete(null);
                loadInvoices();
            },
            (err: any) => {
                console.error('Error deleting invoice:', err);
                setDeleting(false);
            },
            undefined,
            false
        );
    };

    // Handle delete dialog close
    const handleDeleteDialogClose = () => {
        if (!deleting) {
            setDeleteDialogOpen(false);
            setInvoiceToDelete(null);
        }
    };

    // Context menu handlers (right-click)
    const handleContextMenu = (event: React.MouseEvent, invoice: Invoice) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            invoice: invoice,
        });
    };

    const handleContextMenuClose = () => {
        setContextMenu(null);
    };

    // Permanent delete handlers (Administrator only)
    const handlePermanentDeleteClick = () => {
        if (contextMenu) {
            setInvoiceToPermanentDelete(contextMenu.invoice);
            setPermanentDeleteDialogOpen(true);
            handleContextMenuClose();
        }
    };

    const handleConfirmPermanentDelete = () => {
        if (!invoiceToPermanentDelete) return;

        setPermanentDeleting(true);
        call_backend_api(
            InvoiceService.permanentDeleteInvoice(invoiceToPermanentDelete.id),
            () => {
                setPermanentDeleting(false);
                setPermanentDeleteDialogOpen(false);
                setInvoiceToPermanentDelete(null);
                loadInvoices();
            },
            (err: any) => {
                console.error('Error permanently deleting invoice:', err);
                setPermanentDeleting(false);
            },
            undefined,
            true
        );
    };

    const handlePermanentDeleteDialogClose = () => {
        if (!permanentDeleting) {
            setPermanentDeleteDialogOpen(false);
            setInvoiceToPermanentDelete(null);
        }
    };

    // Status menu handlers
    const handleStatusChipClick = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
        event.stopPropagation();
        // Don't allow status change for DRAFT invoices - they must be finalized first
        if (invoice.status === 'DRAFT') return;
        setStatusMenuAnchor(event.currentTarget);
        setStatusMenuInvoice(invoice);
    };

    const handleStatusMenuClose = () => {
        setStatusMenuAnchor(null);
        setStatusMenuInvoice(null);
    };

    const handleStatusChange = (newStatus: string) => {
        if (!statusMenuInvoice) return;

        // For CANCELLED status, show confirmation dialog and create Stornorechnung
        if (newStatus === 'CANCELLED') {
            setInvoiceToStorno(statusMenuInvoice);
            setStornoDialogOpen(true);
            handleStatusMenuClose();
            return;
        }

        call_backend_api(
            InvoiceService.updateInvoiceStatus(statusMenuInvoice.id, newStatus),
            () => {
                loadInvoices();
            },
            (err: any) => {
                console.error('Error updating invoice status:', err);
            },
            undefined,
            true
        );
        handleStatusMenuClose();
    };

    // Generate PDF for storno invoice and upload to backend
    const generateStornoInvoicePdf = async (stornoInvoiceDetail: InvoiceDetail): Promise<void> => {
        try {
            // Map invoice detail to InvoiceData format (includes is_storno and storno_of_invoice_number)
            const invoiceData = mapInvoiceDetailToInvoiceData(stornoInvoiceDetail);

            // Build XRechnung options with buyer data
            const xrechnungOptions: XRechnungOptions = {
                generateXml: true,
                embedInPdf: true,
                buyerData: {
                    name: stornoInvoiceDetail.buyer_name,
                    postalAddress: {
                        streetName: stornoInvoiceDetail.buyer_street || '',
                        cityName: stornoInvoiceDetail.buyer_city || '',
                        postalZone: stornoInvoiceDetail.buyer_postal_code || '',
                        countryCode: stornoInvoiceDetail.buyer_country_code || 'DE',
                    },
                    contact: {
                        email: stornoInvoiceDetail.buyer_contact_email,
                        telephone: stornoInvoiceDetail.buyer_contact_phone,
                        name: stornoInvoiceDetail.buyer_contact_name,
                    },
                },
                buyerReference: stornoInvoiceDetail.buyer_reference || invoiceData.invoiceNumber,
            };

            // Generate PDF using template-based generator
            const result = await createInvoiceFromTemplate(invoiceData, xrechnungOptions);

            // Create file for upload
            const file = new File([result.pdfBlob], result.fileName, {type: 'application/pdf'});

            // Upload PDF to backend
            await new Promise<void>((resolve, reject) => {
                call_backend_api(
                    InvoiceService.uploadInvoicePdf(stornoInvoiceDetail.id, file),
                    () => {
                        console.log('Storno invoice PDF uploaded successfully');
                        resolve();
                    },
                    (err: any) => {
                        console.error('Error uploading storno invoice PDF:', err);
                        reject(err);
                    },
                    undefined,
                    false
                );
            });

            // Also download the PDF for the user
            const url = URL.createObjectURL(result.pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = result.fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error generating storno invoice PDF:', err);
        }
    };

    // Handle Storno confirmation
    const handleStornoConfirm = async () => {
        if (!invoiceToStorno) return;

        const invoiceNumber = parseInt(stornoInvoiceNumber, 10);
        if (isNaN(invoiceNumber) || invoiceNumber < 1) {
            return;
        }

        setCreatingStorno(true);

        try {
            // Create the storno invoice with the provided invoice number
            const stornoInvoice = await new Promise<Invoice>((resolve, reject) => {
                call_backend_api(
                    InvoiceService.createStornoInvoice(invoiceToStorno.id, invoiceNumber),
                    (data: Invoice) => resolve(data),
                    (err: any) => reject(err),
                    undefined,
                    false
                );
            });

            // Fetch full storno invoice details (with line items, emitter, etc.)
            const stornoInvoiceDetail = await new Promise<InvoiceDetail>((resolve, reject) => {
                call_backend_api(
                    InvoiceService.getInvoice(stornoInvoice.id),
                    (data: InvoiceDetail) => resolve(data),
                    (err: any) => reject(err),
                    undefined,
                    false
                );
            });

            // Generate PDF and upload to backend
            await generateStornoInvoicePdf(stornoInvoiceDetail);

            setCreatingStorno(false);
            setStornoDialogOpen(false);
            setInvoiceToStorno(null);
            setStornoInvoiceNumber('');
            loadInvoices();
        } catch (err) {
            console.error('Error creating Stornorechnung:', err);
            setCreatingStorno(false);
        }
    };

    const handleStornoDialogClose = () => {
        if (!creatingStorno) {
            setStornoDialogOpen(false);
            setInvoiceToStorno(null);
            setStornoInvoiceNumber('');
        }
    };

    // Build XRechnung data from invoice detail
    const buildXRechnungData = (invoiceDetail: InvoiceDetail): XRechnungInvoiceData => {
        const emitter = invoiceDetail.emitter as InvoiceEmitter;
        const invoiceNumber = formatInvoiceNumber(invoiceDetail.invoice_number, invoiceDetail.invoice_date, emitter?.short_key);
        const invoiceDate = invoiceDetail.invoice_date ? new Date(invoiceDetail.invoice_date) : new Date();
        const dueDate = invoiceDetail.due_date ? new Date(invoiceDetail.due_date) : new Date(Date.now() + DEFAULT_PAYMENT_DAYS * 24 * 60 * 60 * 1000);

        const xLineItems: XRechnungLineItem[] = invoiceDetail.line_items.map((item, idx) => ({
            id: (idx + 1).toString(),
            name: item.description,  // BT-153: Item name (required)
            note: item.note,         // BT-154: Item description (optional)
            quantity: item.quantity,
            unitCode: item.unit_code,
            unitPrice: item.unit_price,
            lineNetAmount: item.line_net_amount,
            vatCategoryCode: item.vat_category_code || 'S',
            vatRate: item.vat_rate,
        }));

        const xVatBreakdown: VatBreakdown[] = invoiceDetail.vat_breakdown.map(vat => ({
            categoryCode: vat.vat_category_code || 'S',
            rate: vat.vat_rate,
            taxableAmount: vat.taxable_amount,
            taxAmount: vat.tax_amount,
        }));

        return {
            invoiceNumber,
            invoiceDate,
            invoiceTypeCode: '380',
            currencyCode: 'EUR',
            buyerReference: invoiceDetail.buyer_reference || invoiceNumber,
            note: invoiceDetail.note,
            specificationId: XRECHNUNG_SPEC_ID,
            seller: {
                name: emitter?.name || '',
                registrationName: emitter?.name || '',
                identification: {
                    vatId: emitter?.vat_id || '',
                    taxNumber: emitter?.tax_number,
                },
                postalAddress: {
                    streetName: emitter?.street || '',
                    additionalStreetName: emitter?.additional_street,
                    cityName: emitter?.city || '',
                    postalZone: emitter?.postal_code || '',
                    countryCode: emitter?.country_code || 'DE',
                },
                contact: {
                    name: emitter?.contact_name,
                    telephone: emitter?.contact_phone,
                    email: emitter?.contact_email,
                },
                companyId: emitter?.company_id,
            },
            buyer: {
                name: invoiceDetail.buyer_name || '',
                postalAddress: {
                    streetName: invoiceDetail.buyer_street || '',
                    additionalStreetName: invoiceDetail.buyer_additional_street,
                    cityName: invoiceDetail.buyer_city || '',
                    postalZone: invoiceDetail.buyer_postal_code || '',
                    countryCode: invoiceDetail.buyer_country_code || 'DE',
                },
                contact: {
                    email: invoiceDetail.buyer_contact_email,
                    telephone: invoiceDetail.buyer_contact_phone,
                    name: invoiceDetail.buyer_contact_name,
                },
            },
            deliveryDate: invoiceDetail.work_date ? new Date(invoiceDetail.work_date) : undefined,
            deliveryLocation: invoiceDetail.objekt,
            deliveryAddress: {
                streetName: invoiceDetail.objekt || invoiceDetail.buyer_street || '',
                cityName: invoiceDetail.buyer_city || '',
                postalZone: invoiceDetail.buyer_postal_code || '',
                countryCode: invoiceDetail.buyer_country_code || 'DE',
            },
            paymentMeans: {
                paymentMeansCode: '58',
                iban: emitter?.iban || '',
                bic: emitter?.bic,
                accountName: emitter?.account_holder || emitter?.name || '',
            },
            paymentTerms: {
                dueDate,
                note: `Zahlbar innerhalb von ${DEFAULT_PAYMENT_DAYS} Tagen ohne Abzug.`,
            },
            lineItems: xLineItems,
            lineExtensionAmount: invoiceDetail.line_extension_amount || 0,
            taxExclusiveAmount: invoiceDetail.tax_exclusive_amount || 0,
            taxInclusiveAmount: invoiceDetail.tax_inclusive_amount || 0,
            payableAmount: invoiceDetail.payable_amount || 0,
            vatBreakdown: xVatBreakdown,
            objekt: invoiceDetail.objekt,
            durchgefuehrteArbeiten: invoiceDetail.work_description,
            durchgefuehrtAm: invoiceDetail.work_date ? new Date(invoiceDetail.work_date) : undefined,
            // Stornorechnung fields
            is_storno: invoiceDetail.is_storno,
            storno_of_invoice_number: invoiceDetail.storno_of_invoice_number,
        };
    };

    // Map InvoiceDetail to InvoiceData for invoiceGenerator
    const mapInvoiceDetailToInvoiceData = (invoiceDetail: InvoiceDetail): InvoiceData => {
        const emitter = invoiceDetail.emitter as InvoiceEmitter;
        const invoiceNumber = formatInvoiceNumber(invoiceDetail.invoice_number, invoiceDetail.invoice_date, emitter?.short_key);

        // Map line items
        const lineItems: InvoiceGeneratorLineItem[] = invoiceDetail.line_items.map(item => ({
            description: item.description,
            note: item.note || undefined,
            quantity: item.quantity,
            unit: item.unit_code === 'HUR' ? 'Std.' : item.unit_code === 'C62' ? 'Stk.' : item.unit_code,
            unitPrice: item.unit_price,
            total: item.line_net_amount,
        }));

        return {
            invoiceNumber,
            invoiceDate: invoiceDetail.invoice_date ? new Date(invoiceDetail.invoice_date) : new Date(),
            customer: {
                name: invoiceDetail.buyer_name || '',
                street: invoiceDetail.buyer_street || '',
                zipCity: `${invoiceDetail.buyer_postal_code || ''} ${invoiceDetail.buyer_city || ''}`.trim(),
                fullContact: invoiceDetail.buyer_full_contact || undefined,
            },
            emitter: emitter ? {
                short_key: emitter.short_key,
                name: emitter.name,
                street: emitter.street,
                postal_code: emitter.postal_code,
                city: emitter.city,
                country_code: emitter.country_code,
                contact_phone: emitter.contact_phone,
                contact_email: emitter.contact_email,
                website: emitter.website,
                fax: emitter.fax,
                management: emitter.management,
                court_registration: emitter.court_registration,
                po_box: emitter.po_box,
                logo_path: emitter.logo_path,
                iban: emitter.iban,
                bic: emitter.bic,
                bank_name: emitter.bank_name,
                tax_number: emitter.tax_number,
            } : undefined,
            objekt: invoiceDetail.objekt || '',
            durchgefuehrteArbeiten: invoiceDetail.work_description || '',
            durchgefuehrtAm: invoiceDetail.work_date ? new Date(invoiceDetail.work_date) : new Date(),
            lineItems,
            vatRate: 19, // Default VAT rate
            skonto_percentage: invoiceDetail.skonto_percentage,
            skonto_days: invoiceDetail.skonto_days,
            payment_days: invoiceDetail.payment_days ?? 30,
            // Stornorechnung fields
            is_storno: invoiceDetail.is_storno,
            storno_of_invoice_number: invoiceDetail.storno_of_invoice_number,
        };
    };

    // Handle PDF button click - open stored PDF
    const handlePdfClick = async (event: React.MouseEvent, invoice: Invoice) => {
        event.stopPropagation(); // Prevent row click

        // If PDF is stored, open it directly
        if (invoice.pdf_path) {
            // Open the stored PDF in a new tab
            // pdf_path is like /static/invoices/{id}/Rechnung_{id}.pdf
            const baseUrl = import.meta.env.DEV
                ? 'http://localhost:8008'
                : import.meta.env.VITE_BACKEND_URL || '';
            window.open(`${baseUrl}${invoice.pdf_path}`, '_blank');
            return;
        }

        // Fallback: generate PDF on-the-fly for legacy invoices without stored PDF
        setGeneratingPdfFor(invoice.id);

        try {
            // Fetch full invoice details
            const invoiceDetail = await new Promise<InvoiceDetail>((resolve, reject) => {
                call_backend_api(
                    InvoiceService.getInvoice(invoice.id),
                    (data: InvoiceDetail) => resolve(data),
                    (err: any) => reject(err),
                    undefined,
                    false
                );
            });

            // Map invoice detail to InvoiceData format
            const invoiceData = mapInvoiceDetailToInvoiceData(invoiceDetail);

            // Build XRechnung options with buyer data
            const xrechnungOptions: XRechnungOptions = {
                generateXml: true,
                embedInPdf: true,
                buyerData: {
                    name: invoiceDetail.buyer_name,
                    postalAddress: {
                        streetName: invoiceDetail.buyer_street || '',
                        cityName: invoiceDetail.buyer_city || '',
                        postalZone: invoiceDetail.buyer_postal_code || '',
                        countryCode: invoiceDetail.buyer_country_code || 'DE',
                    },
                    contact: {
                        email: invoiceDetail.buyer_contact_email,
                        telephone: invoiceDetail.buyer_contact_phone,
                        name: invoiceDetail.buyer_contact_name,
                    },
                },
                buyerReference: invoiceDetail.buyer_reference || invoiceData.invoiceNumber,
            };

            // Generate PDF using template-based generator
            const result = await createInvoiceFromTemplate(invoiceData, xrechnungOptions);

            // Open PDF in new tab
            const url = URL.createObjectURL(result.pdfBlob);
            window.open(url, '_blank');

            // Clean up URL after a delay
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            console.error('Error generating PDF:', err);
            setError('Fehler beim Erstellen der PDF');
        } finally {
            setGeneratingPdfFor(null);
        }
    };

    const loadInvoices = (page: number = currentPage) => {
        setLoading(true);
        setError(null);

        // When filters are active, load all matching invoices (limit=1000)
        // Otherwise, use pagination with PAGE_SIZE
        const limit = hasActiveServerFilters ? 1000 : PAGE_SIZE;
        const skip = hasActiveServerFilters ? 0 : page * PAGE_SIZE;

        // Build query options
        const options: {
            skip: number;
            limit: number;
            status?: string;
            dateFrom?: string;
            dateTo?: string;
        } = {
            skip,
            limit,
        };

        // Only pass single status to backend (backend doesn't support multiple)
        // If multiple statuses selected, we'll filter client-side
        if (statusFilter.length === 1) {
            options.status = statusFilter[0];
        }

        // Add date filters
        if (dateFrom) {
            options.dateFrom = dateFrom;
        }
        if (dateTo) {
            options.dateTo = dateTo;
        }

        call_backend_api(
            InvoiceService.getInvoices(options),
            (data: InvoiceListResponse) => {
                setInvoices(data.items);
                setTotalInvoices(data.total);
                // Load invoice details to get line items for product amounts
                loadInvoiceDetails(data.items);
                setLoading(false);
            },
            (err: any) => {
                console.error('Error loading invoices:', err);
                setError('Fehler beim Laden der Rechnungen');
                setLoading(false);
            },
            undefined,
            false
        );
    };

    // Handle page change
    const handleChangePage = (_event: unknown, newPage: number) => {
        setCurrentPage(newPage);
        loadInvoices(newPage);
    };

    // Load invoice details to calculate product amounts
    const loadInvoiceDetails = (invoiceList: Invoice[]) => {
        invoiceList.forEach(invoice => {
            call_backend_api(
                InvoiceService.getInvoice(invoice.id),
                (data: InvoiceDetail) => {
                    setInvoiceDetails(prev => {
                        const newMap = new Map(prev);
                        newMap.set(invoice.id, data);
                        return newMap;
                    });
                },
                (err: any) => {
                    console.error(`Error loading invoice detail ${invoice.id}:`, err);
                },
                undefined,
                false
            );
        });
    };

    // Calculate total amount for a specific product in an invoice
    const getProductAmount = (invoiceId: number, productId: number): number | undefined => {
        const detail = invoiceDetails.get(invoiceId);
        if (!detail || !detail.line_items) return undefined;

        const productItems = detail.line_items.filter(item => item.product_id === productId);
        if (productItems.length === 0) return undefined;

        return productItems.reduce((sum, item) => sum + item.line_net_amount, 0);
    };

    // Render dialogs (always rendered, controlled by open state)
    const dialogs = (
        <>
            <EmitterSelectDialog
                open={emitterSelectOpen}
                onClose={handleEmitterSelectClose}
                onSelect={handleEmitterSelect}
            />
            {selectedEmitter && (
                <InvoiceCreateDialog
                    open={invoiceCreateOpen}
                    emitter={selectedEmitter}
                    onClose={handleInvoiceCreateClose}
                    onSave={handleInvoiceSaved}
                    onChangeEmitter={handleChangeEmitter}
                />
            )}
            <InvoiceViewEditDialog
                open={viewEditOpen}
                invoiceId={selectedInvoiceId}
                onClose={handleViewEditClose}
                onSave={handleViewEditSave}
                onDelete={handleViewEditDelete}
            />
            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
                <DialogTitle>Rechnung löschen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sind Sie sicher, dass Sie {invoiceToDelete?.invoice_number
                        ? `Rechnung ${formatInvoiceNumber(invoiceToDelete.invoice_number, invoiceToDelete.invoice_date, getEmitterShortKey(invoiceToDelete.emitter_id))}`
                        : 'diesen Rechnungsentwurf'} löschen möchten?
                        {invoiceToDelete?.buyer_name && (
                            <><br/>Kunde: {invoiceToDelete.buyer_name}</>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} disabled={deleting}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                    >
                        {deleting ? 'Löschen...' : 'Löschen'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Storno (cancellation) confirmation dialog */}
            <Dialog open={stornoDialogOpen} onClose={handleStornoDialogClose}>
                <DialogTitle>Rechnung stornieren</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sind Sie sicher, dass Sie {invoiceToStorno?.invoice_number
                        ? `Rechnung ${formatInvoiceNumber(invoiceToStorno.invoice_number, invoiceToStorno.invoice_date, getEmitterShortKey(invoiceToStorno.emitter_id))}`
                        : 'diese Rechnung'} stornieren möchten?
                        {invoiceToStorno?.buyer_name && (
                            <><br/>Kunde: {invoiceToStorno.buyer_name}</>
                        )}
                        {invoiceToStorno?.payable_amount && (
                            <><br/>Betrag: {formatCurrency(invoiceToStorno.payable_amount)}</>
                        )}
                        <br/><br/>
                        <strong>Hinweis:</strong> Es wird eine Stornorechnung mit negativen Beträgen erstellt
                        und die ursprüngliche Rechnung wird als storniert markiert.
                        <br/>
                        Dieser Schritt kann nicht rückgängig gemacht werden!
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Rechnungsnummer für Stornorechnung"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={stornoInvoiceNumber}
                        onChange={(e) => setStornoInvoiceNumber(e.target.value)}
                        disabled={creatingStorno}
                        inputProps={{min: 1}}
                        helperText="Geben Sie die Rechnungsnummer für die Stornorechnung ein"
                        sx={{mt: 2}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleStornoDialogClose} disabled={creatingStorno}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleStornoConfirm}
                        variant="contained"
                        color="error"
                        disabled={creatingStorno || !stornoInvoiceNumber || parseInt(stornoInvoiceNumber, 10) < 1}
                    >
                        {creatingStorno ? 'Erstelle Stornorechnung...' : 'Stornieren'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Permanent delete confirmation dialog (Administrator only) */}
            <Dialog open={permanentDeleteDialogOpen} onClose={handlePermanentDeleteDialogClose}>
                <DialogTitle sx={{color: 'error.main'}}>Rechnung endgültig löschen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <strong>WARNUNG:</strong> Diese Aktion kann nicht rückgängig gemacht werden!
                        <br/><br/>
                        Sind Sie sicher, dass Sie {invoiceToPermanentDelete?.invoice_number
                        ? `Rechnung ${formatInvoiceNumber(invoiceToPermanentDelete.invoice_number, invoiceToPermanentDelete.invoice_date, getEmitterShortKey(invoiceToPermanentDelete.emitter_id))}`
                        : 'diese Rechnung'} <strong>endgültig</strong> löschen möchten?
                        {invoiceToPermanentDelete?.buyer_name && (
                            <><br/>Kunde: {invoiceToPermanentDelete.buyer_name}</>
                        )}
                        {invoiceToPermanentDelete?.payable_amount && (
                            <><br/>Betrag: {formatCurrency(invoiceToPermanentDelete.payable_amount)}</>
                        )}
                        <br/><br/>
                        Folgende Daten werden unwiderruflich gelöscht:
                        <br/>• Alle Rechnungspositionen
                        <br/>• Alle MwSt.-Aufschlüsselungen
                        <br/>• Die PDF-Datei (falls vorhanden)
                        <br/>• Der Rechnungsdatensatz
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePermanentDeleteDialogClose} disabled={permanentDeleting}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleConfirmPermanentDelete}
                        variant="contained"
                        color="error"
                        disabled={permanentDeleting}
                    >
                        {permanentDeleting ? 'Löschen...' : 'Endgültig löschen'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Context menu for right-click */}
            <Menu
                open={contextMenu !== null}
                onClose={handleContextMenuClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? {top: contextMenu.mouseY, left: contextMenu.mouseX}
                        : undefined
                }
            >
                {isAdministrator && (
                    <MenuItem onClick={handlePermanentDeleteClick}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error"/>
                        </ListItemIcon>
                        <ListItemText primary="Endgültig löschen" primaryTypographyProps={{color: 'error'}}/>
                    </MenuItem>
                )}
            </Menu>
        </>
    );

    // Filter handlers
    const handleProductFilterChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value;
        setSelectedProductIds(typeof value === 'string' ? [] : value);
    };

    const handleStatusFilterChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setStatusFilter(typeof value === 'string' ? [] : value);
    };

    const handleClearFilters = () => {
        setSelectedProductIds([]);
        setStatusFilter([]);
        setDateFrom('');
        setDateTo('');
        setCurrentPage(0);
    };

    const hasActiveFilters = selectedProductIds.length > 0 || statusFilter.length > 0 || dateFrom || dateTo;

    // Filter invoices based on selected filters
    // Note: Date and single status are filtered server-side
    // Multiple status values need client-side filtering
    const filteredInvoices = invoices.filter(invoice => {
        // Client-side status filter (only needed when multiple statuses selected)
        if (statusFilter.length > 1 && !statusFilter.includes(invoice.status)) {
            return false;
        }

        return true;
    });

    // Get selected products for display (or all if none selected)
    const displayProducts = selectedProductIds.length > 0
        ? products.filter(p => selectedProductIds.includes(p.id))
        : [];

    // Header with filters
    const header = (
        <Box sx={{mb: 2}}>
            {/* Filter row */}
            <Paper sx={{p: 2, mb: 2}}>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center'}}>
                    {/* Product columns selector */}
                    <FormControl size="small" sx={{minWidth: 200}}>
                        <InputLabel>Produkt-Spalten</InputLabel>
                        <Select
                            multiple
                            value={selectedProductIds}
                            onChange={handleProductFilterChange}
                            input={<OutlinedInput label="Produkt-Spalten"/>}
                            renderValue={(selected) => {
                                const names = selected
                                    .map(id => products.find(p => p.id === id)?.name)
                                    .filter(Boolean);
                                return names.join(', ');
                            }}
                        >
                            {products.map((product) => (
                                <MenuItem key={product.id} value={product.id}>
                                    <Checkbox checked={selectedProductIds.includes(product.id)}/>
                                    <ListItemText primary={product.name}/>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Status filter */}
                    <FormControl size="small" sx={{minWidth: 150}}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            multiple
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            input={<OutlinedInput label="Status"/>}
                            renderValue={(selected) => selected.map(s => getStatusLabel(s)).join(', ')}
                        >
                            {allStatuses.map((status) => (
                                <MenuItem key={status} value={status}>
                                    <Checkbox checked={statusFilter.includes(status)}/>
                                    <ListItemText primary={getStatusLabel(status)}/>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Date from */}
                    <TextField
                        size="small"
                        type="date"
                        label="Datum von"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{shrink: true}}
                        sx={{width: 150}}
                    />

                    {/* Date to */}
                    <TextField
                        size="small"
                        type="date"
                        label="Datum bis"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{shrink: true}}
                        sx={{width: 150}}
                    />

                    {/* Clear filters button */}
                    {hasActiveFilters && (
                        <Tooltip title="Filter zurücksetzen">
                            <IconButton onClick={handleClearFilters} size="small">
                                <ClearIcon/>
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Spacer */}
                    <Box sx={{flexGrow: 1}}/>

                    {/* New invoice button */}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon/>}
                        onClick={handleNewInvoice}
                    >
                        Neue Rechnung
                    </Button>
                </Box>
            </Paper>
        </Box>
    );

    if (loading) {
        return (
            <>
                {header}
                <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                    <CircularProgress/>
                </Box>
                {dialogs}
            </>
        );
    }

    if (error) {
        return (
            <>
                {header}
                <Alert severity="error" sx={{mt: 2}}>
                    {error}
                </Alert>
                {dialogs}
            </>
        );
    }

    if (invoices.length === 0) {
        return (
            <>
                {header}
                <Box sx={{py: 4, textAlign: 'center'}}>
                    <Typography variant="body1" color="text.secondary">
                        Noch keine Rechnungen vorhanden.
                    </Typography>
                </Box>
                {dialogs}
            </>
        );
    }

    if (filteredInvoices.length === 0 && hasActiveFilters) {
        return (
            <>
                {header}
                <Box sx={{py: 4, textAlign: 'center'}}>
                    <Typography variant="body1" color="text.secondary">
                        Keine Rechnungen entsprechen den Filterkriterien.
                    </Typography>
                    <Button
                        variant="text"
                        onClick={handleClearFilters}
                        sx={{mt: 1}}
                    >
                        Filter zurücksetzen
                    </Button>
                </Box>
                {dialogs}
            </>
        );
    }

    return (
        <>
            {header}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Rechnungsnr.</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Datum</TableCell>
                            <TableCell>Kunde</TableCell>
                            {/* Dynamic product columns - only show selected products */}
                            {displayProducts.map((product) => (
                                <TableCell key={product.id} align="right">
                                    {product.name}
                                </TableCell>
                            ))}
                            <TableCell align="right">Netto</TableCell>
                            <TableCell align="right">Brutto</TableCell>
                            <TableCell width={60}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredInvoices.map((invoice) => (
                            <TableRow
                                key={invoice.id}
                                hover
                                sx={{cursor: 'pointer'}}
                                onClick={() => handleInvoiceClick(invoice.id)}
                                onContextMenu={(e) => handleContextMenu(e, invoice)}
                            >
                                <TableCell>
                                    {formatInvoiceNumber(invoice.invoice_number, invoice.invoice_date, getEmitterShortKey(invoice.emitter_id))}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={getStatusLabel(invoice.status)}
                                        color={getStatusColor(invoice.status)}
                                        size="small"
                                        onClick={invoice.status !== 'DRAFT' && invoice.status !== 'CANCELLED' && !invoice.is_storno ? (e) => handleStatusChipClick(e, invoice) : undefined}
                                        sx={{cursor: invoice.status !== 'DRAFT' && invoice.status !== 'CANCELLED' && !invoice.is_storno ? 'pointer' : 'default'}}
                                    />
                                </TableCell>
                                <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                                <TableCell>{invoice.buyer_full_contact ? invoice.buyer_full_contact.split('\n')[0] + ' ' + invoice.buyer_full_contact.split('\n')[1] + '...' : invoice.buyer_name}</TableCell>
                                {/* Dynamic product amount columns - only show selected products */}
                                {displayProducts.map((product) => {
                                    const amount = getProductAmount(invoice.id, product.id);
                                    return (
                                        <TableCell key={product.id} align="right">
                                            {amount !== undefined ? formatCurrency(amount) : '-'}
                                        </TableCell>
                                    );
                                })}
                                <TableCell align="right">
                                    {formatCurrency(invoice.tax_exclusive_amount)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCurrency(invoice.payable_amount)}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{display: 'flex', gap: 0.5}}>
                                        {invoice.status !== 'DRAFT' && (
                                            <Tooltip title="PDF öffnen">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => handlePdfClick(e, invoice)}
                                                    disabled={generatingPdfFor === invoice.id}
                                                >
                                                    {generatingPdfFor === invoice.id ? (
                                                        <CircularProgress size={18}/>
                                                    ) : (
                                                        <PictureAsPdfIcon fontSize="small"/>
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {invoice.status === 'DRAFT' && (
                                            <Tooltip title="Löschen">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => handleDeleteClick(e, invoice)}
                                                >
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    {/* Sum row */}
                    <TableFooter>
                        <TableRow sx={{'& td': {fontWeight: 'bold', fontSize: '0.875rem'}}}>
                            <TableCell colSpan={4}>Summe</TableCell>
                            {/* Sum for each displayed product column */}
                            {displayProducts.map((product) => {
                                const productSum = filteredInvoices.reduce((sum, invoice) => {
                                    const amount = getProductAmount(invoice.id, product.id);
                                    return sum + (amount || 0);
                                }, 0);
                                return (
                                    <TableCell key={product.id} align="right">
                                        {formatCurrency(productSum)}
                                    </TableCell>
                                );
                            })}
                            {/* Sum for Netto column */}
                            <TableCell align="right">
                                {formatCurrency(
                                    filteredInvoices.reduce((sum, invoice) =>
                                        sum + (invoice.tax_exclusive_amount || 0), 0
                                    )
                                )}
                            </TableCell>
                            {/* Sum for Brutto column */}
                            <TableCell align="right">
                                {formatCurrency(
                                    filteredInvoices.reduce((sum, invoice) =>
                                        sum + (invoice.payable_amount || 0), 0
                                    )
                                )}
                            </TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {!hasActiveServerFilters && (
                <TablePagination
                    component="div"
                    count={totalInvoices}
                    page={currentPage}
                    onPageChange={handleChangePage}
                    rowsPerPage={PAGE_SIZE}
                    rowsPerPageOptions={[PAGE_SIZE]}
                    labelDisplayedRows={({from, to, count}) =>
                        `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
                    }
                />
            )}
            {hasActiveServerFilters && (
                <Box sx={{py: 1, px: 2}}>
                    <Typography variant="body2" color="text.secondary">
                        {filteredInvoices.length} Rechnung{filteredInvoices.length !== 1 ? 'en' : ''} gefunden
                    </Typography>
                </Box>
            )}

            {dialogs}

            {/* Status change menu */}
            <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={handleStatusMenuClose}
            >
                <MenuItem
                    onClick={() => handleStatusChange('FINALIZED')}
                    disabled={statusMenuInvoice?.status === 'FINALIZED'}
                >
                    {getStatusLabel('FINALIZED')}
                </MenuItem>
                <MenuItem
                    onClick={() => handleStatusChange('SENT')}
                    disabled={statusMenuInvoice?.status === 'SENT'}
                >
                    {getStatusLabel('SENT')}
                </MenuItem>
                <MenuItem
                    onClick={() => handleStatusChange('PAID')}
                    disabled={statusMenuInvoice?.status === 'PAID'}
                >
                    {getStatusLabel('PAID')}
                </MenuItem>
                <MenuItem
                    onClick={() => handleStatusChange('CANCELLED')}
                    disabled={statusMenuInvoice?.status === 'CANCELLED'}
                >
                    {getStatusLabel('CANCELLED')}
                </MenuItem>
            </Menu>
        </>
    );
};

export default RechnungenTab;
